// src/components/common/DialogProvider.tsx
// Branded replacement for window.alert / window.confirm / window.prompt.
//
// Wrap the app once:   <DialogProvider> ... </DialogProvider>
// Then from any component:
//
//   const dialog = useDialog();
//   await dialog.alert({ title: "Saved", message: "All done." });
//   const ok = await dialog.confirm({
//     title: "Delete campaign?",
//     message: "This can't be undone.",
//     tone: "danger",
//     confirmText: "Delete",
//   });
//   const reason = await dialog.prompt({
//     title: "Reject withdrawal",
//     message: "The subscriber will see this reason.",
//     placeholder: "Reason…",
//   }); // resolves to the string, or null when cancelled
//
// All three return promises so existing `if (!window.confirm(...)) return;`
// flows convert 1:1 with `if (!(await dialog.confirm({...}))) return;`.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, HelpCircle, Info, X } from "lucide-react";

export type DialogTone = "info" | "success" | "warning" | "danger";

export interface AlertOptions {
  title: string;
  message?: string;
  tone?: DialogTone;
  /** Label for the dismiss button (default "OK") */
  confirmText?: string;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  /** "danger" turns the confirm button red for destructive actions */
  tone?: DialogTone;
  confirmText?: string;
  cancelText?: string;
}

export interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  /** When true an empty input keeps the confirm button disabled */
  required?: boolean;
  multiline?: boolean;
  tone?: DialogTone;
  confirmText?: string;
  cancelText?: string;
}

interface DialogApi {
  alert: (opts: AlertOptions) => Promise<void>;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

type ActiveDialog =
  | { kind: "alert"; opts: AlertOptions; resolve: () => void }
  | { kind: "confirm"; opts: ConfirmOptions; resolve: (ok: boolean) => void }
  | {
      kind: "prompt";
      opts: PromptOptions;
      resolve: (value: string | null) => void;
    };

const DialogContext = createContext<DialogApi | null>(null);

const TONE_STYLES: Record<
  DialogTone,
  { iconBg: string; iconColor: string; Icon: React.ElementType }
> = {
  info: { iconBg: "bg-purple-100", iconColor: "text-purple-600", Icon: Info },
  success: {
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    Icon: CheckCircle2,
  },
  warning: {
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    Icon: AlertTriangle,
  },
  danger: {
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    Icon: AlertTriangle,
  },
};

const CONFIRM_BUTTON: Record<DialogTone, string> = {
  info: "bg-purple-600 hover:bg-purple-700 focus-visible:ring-purple-500",
  success: "bg-green-600 hover:bg-green-700 focus-visible:ring-green-500",
  warning: "bg-amber-500 hover:bg-amber-600 focus-visible:ring-amber-400",
  danger: "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500",
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [active, setActive] = useState<ActiveDialog | null>(null);
  const [visible, setVisible] = useState(false);
  const [promptValue, setPromptValue] = useState("");
  const primaryRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const open = useCallback((dialog: ActiveDialog) => {
    setActive(dialog);
    setPromptValue(
      dialog.kind === "prompt" ? dialog.opts.defaultValue || "" : "",
    );
    // let the element mount before starting the enter transition
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const alert = useCallback(
    (opts: AlertOptions) =>
      new Promise<void>((resolve) => open({ kind: "alert", opts, resolve })),
    [open],
  );
  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) =>
        open({ kind: "confirm", opts, resolve }),
      ),
    [open],
  );
  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) =>
        open({ kind: "prompt", opts, resolve }),
      ),
    [open],
  );

  const close = useCallback(
    (settle: () => void) => {
      setVisible(false);
      // matches the leave transition duration below
      window.setTimeout(() => {
        settle();
        setActive(null);
      }, 150);
    },
    [],
  );

  const handleCancel = useCallback(() => {
    if (!active) return;
    if (active.kind === "alert") close(() => active.resolve());
    else if (active.kind === "confirm") close(() => active.resolve(false));
    else close(() => active.resolve(null));
  }, [active, close]);

  const handleConfirm = useCallback(() => {
    if (!active) return;
    if (active.kind === "alert") close(() => active.resolve());
    else if (active.kind === "confirm") close(() => active.resolve(true));
    else {
      const value = promptValue;
      if (active.opts.required && !value.trim()) return;
      close(() => active.resolve(value));
    }
  }, [active, close, promptValue]);

  // Focus the input (prompt) or primary button once open, and support
  // Escape to cancel from anywhere in the dialog.
  useEffect(() => {
    if (!active || !visible) return;
    const focusTarget =
      active.kind === "prompt"
        ? active.opts.multiline
          ? textareaRef.current
          : inputRef.current
        : primaryRef.current;
    focusTarget?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, visible, handleCancel]);

  const tone: DialogTone =
    active?.opts.tone || (active?.kind === "confirm" ? "warning" : "info");
  const { iconBg, iconColor, Icon } = TONE_STYLES[tone];

  const confirmDisabled =
    active?.kind === "prompt" &&
    !!active.opts.required &&
    !promptValue.trim();

  return (
    <DialogContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      {active &&
        createPortal(
          <div
            className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 transition-opacity duration-150 ${
              visible ? "opacity-100" : "opacity-0"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="lk-dialog-title"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-gray-900/50 backdrop-blur-[2px]"
              onClick={handleCancel}
            />

            {/* Card */}
            <div
              className={`relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 transition-transform duration-150 ${
                visible ? "translate-y-0 scale-100" : "translate-y-3 scale-95"
              }`}
            >
              <button
                onClick={handleCancel}
                aria-label="Close"
                className="absolute top-3.5 right-3.5 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X size={16} />
              </button>

              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div
                    className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}
                  >
                    {active.kind === "prompt" && !active.opts.tone ? (
                      <HelpCircle size={22} className="text-purple-600" />
                    ) : (
                      <Icon size={22} className={iconColor} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <h3
                      id="lk-dialog-title"
                      className="text-base font-semibold text-gray-900"
                    >
                      {active.opts.title}
                    </h3>
                    {active.opts.message && (
                      <p className="mt-1.5 text-sm text-gray-600 whitespace-pre-line">
                        {active.opts.message}
                      </p>
                    )}

                    {active.kind === "prompt" &&
                      (active.opts.multiline ? (
                        <textarea
                          ref={textareaRef}
                          value={promptValue}
                          onChange={(e) => setPromptValue(e.target.value)}
                          placeholder={active.opts.placeholder}
                          rows={3}
                          className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                        />
                      ) : (
                        <input
                          ref={inputRef}
                          value={promptValue}
                          onChange={(e) => setPromptValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleConfirm();
                            }
                          }}
                          placeholder={active.opts.placeholder}
                          className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      ))}
                  </div>
                </div>
              </div>

              <div className="px-6 pb-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                {active.kind !== "alert" && (
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2.5 sm:py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {active.opts.cancelText || "Cancel"}
                  </button>
                )}
                <button
                  ref={primaryRef}
                  onClick={handleConfirm}
                  disabled={confirmDisabled}
                  className={`px-4 py-2.5 sm:py-2 rounded-lg text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${CONFIRM_BUTTON[tone]}`}
                >
                  {active.opts.confirmText ||
                    (active.kind === "alert" ? "OK" : "Confirm")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogApi => {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog must be used inside <DialogProvider>");
  }
  return ctx;
};

export default DialogProvider;
